import {useState, useEffect} from 'react';
import {Helmet} from 'react-helmet-async';
// @mui
import {
    Box,
    Card,
    Container,
    List,
    ListItem,
    Link,
    ListItemText,
    Stack,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';

import TERMS_DATA from '../config/terms-of-service.json';

// ----------------------------------------------------------------------

export default function TermsOfServicePage() {
    const [selectedVersion, setSelectedVersion] = useState(TERMS_DATA[0].version);
    const [currentTerms, setCurrentTerms] = useState(TERMS_DATA[0]);

    useEffect(() => {
        const terms = TERMS_DATA.find(t => t.version === selectedVersion);
        if (terms) {
            setCurrentTerms(terms);
        }
    }, [selectedVersion]);

    const handleVersionChange = (event) => {
        setSelectedVersion(event.target.value);
    };

    const {title, updatedDate, sections} = currentTerms;

    return (<>
        <Helmet>
            <title> {`${title} | CibusAI`} </title>
        </Helmet>

        <Box
            sx={{
                bgcolor: 'background.neutral', py: {xs: 6, md: 10}, borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            <Container maxWidth="lg">
                <Stack direction={{xs: 'column', md: 'row'}} justifyContent="space-between" alignItems={{xs: 'flex-start', md: 'center'}} spacing={3}>
                    <Stack spacing={1}>
                        <Typography
                            variant="overline"
                            sx={{color: 'primary.main', fontWeight: 700, letterSpacing: 1.2}}
                        >
                            Legal
                        </Typography>
                        <Typography variant="h2" sx={{fontWeight: 800}}>
                            {title}
                        </Typography>
                        <Typography variant="body2" sx={{color: 'text.secondary'}}>
                            Last updated: {updatedDate}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <FormControl sx={{minWidth: 150}}>
                            <InputLabel id="version-select-label">Version</InputLabel>
                            <Select
                                labelId="version-select-label"
                                id="version-select"
                                value={selectedVersion}
                                label="Version"
                                onChange={handleVersionChange}
                                sx={{bgcolor: 'background.paper'}}
                            >
                                {TERMS_DATA.map((terms) => (
                                    <MenuItem key={terms.version} value={terms.version}>
                                        Version {terms.version}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
            </Container>
        </Box>

        <Container maxWidth="md" sx={{py: 8}}>
            <Card
                variant="outlined"
                sx={{
                    p: {xs: 3, md: 6}, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 'none',
                }}
            >
                <Stack spacing={4}>
                    {sections.map((section, index) => (<Box key={section.title}>
                        <Typography
                            variant="h5"
                            sx={{mb: 2, fontWeight: 700}}
                        >
                            {index + 1}. {section.title}
                        </Typography>

                        {section.content && (<Typography
                            variant="body1"
                            component="div"
                            sx={{
                                color: 'text.secondary', whiteSpace: 'pre-wrap',
                            }}
                        >
                            {section.content}

                            {section.contactDetails && (
                                <Box
                                    sx={{
                                        mt: 3,
                                        p: 3,
                                        borderRadius: 2,
                                        bgcolor: 'background.neutral',
                                        border: (theme) => `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Stack spacing={1.5}>
                                        <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                                            {section.contactDetails.company}
                                        </Typography>

                                        <Typography variant="body1" color="text.secondary">
                                            Website:{' '}
                                            <Link
                                                href={`https://${section.contactDetails.website}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {section.contactDetails.website}
                                            </Link>
                                        </Typography>

                                        <Typography variant="body1" color="text.secondary">
                                            Email:{' '}
                                            <Link href={`mailto:${section.contactDetails.email}`}>
                                                {section.contactDetails.email}
                                            </Link>
                                        </Typography>
                                    </Stack>
                                </Box>
                            )}
                        </Typography>)}

                        {section.paragraphs?.map((paragraph) => (<Typography
                            key={paragraph}
                            variant="body1"
                            sx={{
                                color: 'text.secondary', mb: 2,
                            }}
                        >
                            {paragraph}
                        </Typography>))}

                        {section.bulletPoints && (<List
                            component="ul"
                            sx={{
                                pl: 4, py: 0, listStyleType: 'disc', color: 'text.secondary',
                            }}
                        >
                            {section.bulletPoints.map((item) => (<ListItem
                                key={item}
                                component="li"
                                sx={{
                                    display: 'list-item', py: 0.5, pl: 0,
                                }}
                            >
                                <ListItemText
                                    primary={item}
                                    primaryTypographyProps={{
                                        variant: 'body1', color: 'text.secondary',
                                    }}
                                />
                            </ListItem>))}
                        </List>)}

                        {section.trailingParagraphs?.map((paragraph) => (<Typography
                            key={paragraph}
                            variant="body1"
                            sx={{
                                color: 'text.secondary', mt: 2,
                            }}
                        >
                            {paragraph}
                        </Typography>))}
                    </Box>))}
                </Stack>
            </Card>
        </Container>
    </>);
}
